import React from 'react';
import { useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { type PopoverOrigin } from '@mui/material/Popover';
import {
  LogoutOutlined,
  LightModeOutlined,
  DarkModeOutlined,
  SettingsBrightnessOutlined,
  Check,
  AddAPhotoOutlined,
} from '@mui/icons-material';
import { useAuth } from '../../Context/AuthenticationContext';
import { useThemeMode, type ThemeMode } from '../../theme';
import { type UserIdentity } from './userIdentity';

const themeModes: { value: ThemeMode; label: string; icon: React.ReactElement }[] = [
  { value: 'light', label: 'Light', icon: <LightModeOutlined fontSize="small" /> },
  { value: 'dark', label: 'Dark', icon: <DarkModeOutlined fontSize="small" /> },
  { value: 'system', label: 'System', icon: <SettingsBrightnessOutlined fontSize="small" /> },
];

export function UserAvatar({ identity, size = 32 }: { identity: UserIdentity; size?: number }) {
  const theme = useTheme();
  return (
    <Avatar
      src={identity.photoUrl}
      alt={identity.name}
      sx={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.4),
        fontWeight: 600,
        bgcolor: theme.tokens.accent,
        color: theme.tokens.fgOnAccent,
      }}
    >
      {identity.initials}
    </Avatar>
  );
}

interface UserAccountMenuProps {
  identity: UserIdentity;
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  anchorOrigin: PopoverOrigin;
  transformOrigin: PopoverOrigin;
}

export function UserAccountMenu({
  identity,
  anchorEl,
  open,
  onClose,
  anchorOrigin,
  transformOrigin,
}: UserAccountMenuProps) {
  const { logout } = useAuth();
  const { mode, setMode } = useThemeMode();

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      slotProps={{ paper: { sx: { minWidth: 240, maxWidth: 300 } } }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px' }}>
        <UserAvatar identity={identity} size={40} />
        <div style={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {identity.name}
          </Typography>
          {identity.email && (
            <Typography variant="caption" color="text.secondary" display="block" noWrap>
              {identity.email}
            </Typography>
          )}
        </div>
      </div>

      <Divider />

      <MenuItem
        onClick={() => {
          onClose();
          window.open('https://gravatar.com', '_blank', 'noopener,noreferrer');
        }}
      >
        <ListItemIcon>
          <AddAPhotoOutlined fontSize="small" />
        </ListItemIcon>
        <ListItemText>Set photo on Gravatar</ListItemText>
      </MenuItem>

      <Divider />

      {themeModes.map(t => (
        <MenuItem key={t.value} onClick={() => setMode(t.value)} selected={mode === t.value}>
          <ListItemIcon>{t.icon}</ListItemIcon>
          <ListItemText>{t.label}</ListItemText>
          {mode === t.value && <Check fontSize="small" color="primary" />}
        </MenuItem>
      ))}

      <Divider />

      <MenuItem
        onClick={() => {
          onClose();
          logout();
        }}
      >
        <ListItemIcon>
          <LogoutOutlined fontSize="small" />
        </ListItemIcon>
        <ListItemText>Sign out</ListItemText>
      </MenuItem>
    </Menu>
  );
}
